import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, UserRound, Users, ListTodo, BookOpen, UsersRound, CalendarDays, FileText, Compass, CornerDownLeft, UserPlus } from "lucide-react";
import { QuickClientModal } from "./QuickClientModal";

const TYPE_META = {
  client: { label: "Clients", icon: Users },
  staff: { label: "Staff", icon: UserRound },
  task: { label: "Tasks", icon: ListTodo },
  appointment: { label: "Appointments", icon: CalendarDays },
  document: { label: "Documents", icon: FileText },
  resource: { label: "Education", icon: BookOpen },
  group: { label: "Groups", icon: UsersRound },
  page: { label: "Pages & Modules", icon: Compass },
};
const TYPE_ORDER = ["client", "staff", "task", "appointment", "document", "group", "resource", "page"];

const PAGES = [
  ["Dashboard", "/"], ["All Clients", "/clients/all"], ["Clients Workspace", "/clients"], ["Scheduler", "/scheduler"],
  ["Tasks", "/tasks"], ["Groups", "/groups"], ["Reports", "/reports"], ["Revenue Cycle", "/revenue"],
  ["Complete Chart", "/clinical/documents"], ["Clinical Outcome Measures", "/outcome-measures"],
  ["Education Resources", "/resources"], ["Portal Requests", "/requests"], ["Feedback & Surveys", "/portal-feedback"],
  ["Settings", "/settings"], ["User Management", "/users"], ["Security & 2FA", "/security"], ["Preferences", "/preferences"],
  ["Help & Knowledge Base", "/help"], ["Client Entrance Survey", "/engagement/entrance-survey"],
  ["Intake / Add New Client", "/engagement/intake/new"], ["Intake Tasks", "/engagement/intake-tasks"],
  ["Referrals", "/engagement/referrals"], ["Insurance Verification", "/engagement/insurance-verification"],
  ["Letters & Messaging", "/letters"], ["Print Center", "/print"], ["Census", "/census"], ["Caseload", "/caseload"],
];

export function GlobalSearch({ mobile = false }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("all");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [quickOpen, setQuickOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); setOpen(false); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setResults(r.data.results); setCat("all"); setActiveIdx(-1); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const all = useMemo(() => {
    if (results === null) return null;
    const s = q.trim().toLowerCase();
    const pages = PAGES.filter(([label]) => label.toLowerCase().includes(s)).slice(0, 5)
      .map(([label, link]) => ({ type: "page", id: link, label, sub: link, link }));
    return [...results, ...pages];
  }, [results, q]);

  const counts = useMemo(() => (all || []).reduce((acc, r) => { acc[r.type] = (acc[r.type] || 0) + 1; return acc; }, {}), [all]);
  const visible = useMemo(() => (all || []).filter((r) => cat === "all" || r.type === cat), [all, cat]);
  const grouped = useMemo(() => {
    const g = visible.reduce((acc, r) => { (acc[r.type] = acc[r.type] || []).push(r); return acc; }, {});
    return TYPE_ORDER.filter((t) => g[t]).map((t) => [t, g[t]]);
  }, [visible]);
  const flat = useMemo(() => grouped.flatMap(([, rows]) => rows), [grouped]);

  const go = (r) => { if (!r.link) return; setOpen(false); setQ(""); navigate(r.link); };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setQ(""); return; }
    if (!open || !flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? flat.length - 1 : i - 1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); go(flat[activeIdx]); }
    else if (e.key === "Tab" && grouped.length > 1) {
      e.preventDefault();
      const cats = ["all", ...Object.keys(counts)];
      setCat((c) => cats[(cats.indexOf(c) + 1) % cats.length]);
      setActiveIdx(-1);
    }
  };

  const clientMatches = useMemo(() => (results || []).filter((r) => r.type === "client").length, [results]);
  const showQuickCreate = q.trim().length >= 2 && clientMatches === 0 && results !== null;

  return (
    <div className={mobile ? "relative w-full" : "relative hidden md:block w-64 lg:w-80"} ref={boxRef}>
      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50" />
      <input data-testid={mobile ? "global-search-input-mobile" : "global-search-input"} value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search clients, tasks, documents, pages…"
        className="h-10 w-full rounded-full bg-white/10 pl-10 pr-4 text-sm text-white placeholder-white/50 border border-white/10 focus:bg-white/15 focus:border-white/30 focus:outline-none transition-colors" />
      {open && all && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-12 z-[95] max-h-[70vh] overflow-y-auto rounded-2xl border border-[#EFEBE0] bg-white text-[#3E2723] shadow-[0_16px_48px_-12px_rgba(62,39,35,0.35)] pb-2">
          {showQuickCreate && (
            <button type="button" data-testid="search-quick-create-client"
              onClick={() => { setQuickOpen(true); setOpen(false); }}
              className="flex w-full items-center gap-3 border-b border-[#F1EEE7] bg-[#FDFCF8] px-4 py-3 text-left hover:bg-[#F5F2EB]">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#4E342E] text-white">
                <UserPlus size={16} />
              </div>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-[#3E2723]">Quick create &quot;{q}&quot; as new client</span>
                <span className="block truncate text-[11px] text-[#7B6157]">No matching client. Start a chart in under 30 seconds without leaving this page.</span>
              </span>
            </button>
          )}
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-[#F1EEE7] bg-white px-3 py-2" data-testid="search-category-tabs">
            <button data-testid="search-cat-all" onClick={() => { setCat("all"); setActiveIdx(-1); }}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${cat === "all" ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#7B6157] hover:bg-[#EDE7DC]"}`}>
              All {all.length}
            </button>
            {TYPE_ORDER.filter((t) => counts[t]).map((t) => (
              <button key={t} data-testid={`search-cat-${t}`} onClick={() => { setCat(t); setActiveIdx(-1); }}
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${cat === t ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#7B6157] hover:bg-[#EDE7DC]"}`}>
                {TYPE_META[t].label} {counts[t]}
              </button>
            ))}
          </div>
          {visible.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[#84675B]">No matches for &quot;{q}&quot;</div>
          ) : grouped.map(([type, rows]) => {
            const M = TYPE_META[type] || TYPE_META.client;
            return (
              <div key={type}>
                <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#84675B]">
                  <M.icon size={11} /> {M.label} <span className="rounded-full bg-[#F5F2EB] px-1.5">{rows.length}</span>
                </div>
                {rows.map((r) => {
                  const idx = flat.indexOf(r);
                  return (
                    <button key={`${type}-${r.id}`} data-testid={`search-result-${type}-${r.id}`} onClick={() => go(r)} disabled={!r.link}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${idx === activeIdx ? "bg-[#F5F2EB]" : ""} ${r.link ? "cursor-pointer" : "opacity-60 cursor-default"}`}>
                      <M.icon size={15} className="shrink-0 text-[#7B6157]" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold">{r.label}</span>
                        {r.sub && <span className="block truncate text-[11px] text-[#84675B] capitalize">{r.sub}</span>}
                      </span>
                      {idx === activeIdx && <CornerDownLeft size={12} className="shrink-0 text-[#84675B]" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div className="mt-1 border-t border-[#F1EEE7] px-4 pt-2 text-[10px] text-[#84675B]">↑↓ navigate · Enter open · Tab switch category · Esc close</div>
        </div>
      )}
      <QuickClientModal open={quickOpen} initialName={q}
        onClose={() => setQuickOpen(false)}
        onCreated={(cl) => { setQuickOpen(false); setQ(""); setOpen(false); navigate(`/clients/${cl.id}/console`); }} />
    </div>
  );
}
